import React, { useState } from 'react';

export default function SitesPanel({ apps, onEdit, onRemove, onOpenWindow, onCheck, onCheckAll }) {
  const [sortBy, setSortBy] = useState('title'); // title, status, lastChecked
  const [expandedId, setExpandedId] = useState(null);

  // Status colors and icons
  const getStatusStyle = (status) => {
    if (!status) return { color: '#9ca3af', icon: '◯', label: 'Non vérifié' };
    const s = String(status);
    if (s === 'online' || s === '200' || s.startsWith('2')) return { color: '#10b981', icon: '●', label: 'En ligne' };
    if (s === 'unknown') return { color: '#f59e0b', icon: '◔', label: 'Inconnu' };
    return { color: '#ef4444', icon: '●', label: 'Hors ligne' };
  };

  const sortedApps = [...apps].sort((a, b) => {
    // Support both 'title' (for apps) and 'name' (for sites)
    const aTitle = (a.title || a.name || '').toString();
    const bTitle = (b.title || b.name || '').toString();
    
    if (sortBy === 'title') return aTitle.localeCompare(bTitle);
    if (sortBy === 'status') {
      const aStatus = getStatusStyle(a.status);
      const bStatus = getStatusStyle(b.status);
      return bStatus.label.localeCompare(aStatus.label);
    }
    if (sortBy === 'lastChecked') {
      return (b.lastChecked || 0) - (a.lastChecked || 0);
    }
    return 0;
  });

  return (
    <aside className="sites-panel">
      <div className="sites-panel-header">
        <div className="sites-panel-title">
          <h3>📍 Sites enregistrés</h3>
          <span className="sites-count">{apps.length}</span>
        </div>
        <div className="sites-panel-controls">
          <select
            className="sites-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="title">Trier par titre</option>
            <option value="status">Trier par statut</option>
            <option value="lastChecked">Plus récents</option>
          </select>
          <button className="btn btn-sm" title="Vérifier tous" onClick={onCheckAll}>
            🔄
          </button>
        </div>
      </div>

      <div className="sites-list">
        {apps.length === 0 ? (
          <div className="sites-empty">
            <div className="sites-empty-icon">📭</div>
            <p>Aucun site enregistré</p>
            <p className="sites-empty-hint">Cliquez sur le + pour en ajouter</p>
          </div>
        ) : (
          sortedApps.map((app) => {
            const statusInfo = getStatusStyle(app.status);
            const isExpanded = expandedId === app.id;

            return (
              <div key={app.id} className="site-card liquid">
                <div className="site-card-header" onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                  <div className="site-card-icon">
                    <img
                      src={
                        app.icon ||
                        (app.url ? new URL(app.url).origin + '/favicon.ico' : '/favicon.ico')
                      }
                      alt={app.title || app.name}
                      onError={(e) => (e.currentTarget.src = '/favicon.ico')}
                    />
                  </div>

                  <div className="site-card-info">
                    <div className="site-card-title">{app.title || app.name}</div>
                    <div className="site-card-url">{new URL(app.url).hostname}</div>
                  </div>

                  <div className="site-card-status">
                    <span
                      className="status-indicator"
                      style={{ color: statusInfo.color }}
                      title={`${statusInfo.label}${app.statusVia ? ` (via ${app.statusVia})` : ''}`}
                    >
                      {statusInfo.icon}
                    </span>
                  </div>

                  <div className="site-card-expand">
                    {isExpanded ? '▼' : '▶'}
                  </div>
                </div>

                {isExpanded && (
                  <div className="site-card-details">
                    {app.description && (
                      <div className="site-detail-row">
                        <span className="site-detail-label">Description:</span>
                        <span className="site-detail-value">{app.description}</span>
                      </div>
                    )}

                    <div className="site-detail-row">
                      <span className="site-detail-label">URL:</span>
                      <span className="site-detail-value" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                        {app.url}
                      </span>
                    </div>

                    <div className="site-detail-row">
                      <span className="site-detail-label">Statut:</span>
                      <span
                        className="site-detail-value"
                        style={{ color: statusInfo.color, fontWeight: '600' }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>

                    {app.lastChecked && (
                      <div className="site-detail-row">
                        <span className="site-detail-label">Vérifié:</span>
                        <span className="site-detail-value">
                          {new Date(app.lastChecked).toLocaleString()}
                        </span>
                      </div>
                    )}

                    <div className="site-card-actions">
                      <button
                        className="site-action-btn site-action-primary liquid-button"
                        onClick={() => onOpenWindow(app)}
                        title="Ouvrir dans une fenêtre"
                      >
                        🪟 Ouvrir
                      </button>
                      <button
                        className="site-action-btn site-action-secondary liquid-button"
                        onClick={() => onCheck(app.id)}
                        title="Vérifier le statut"
                      >
                        🔍 Vérifier
                      </button>
                      <button
                        className="site-action-btn site-action-edit liquid-button"
                        onClick={() => onEdit(app)}
                        title="Éditer"
                      >
                        ✏️ Éditer
                      </button>
                      <button
                        className="site-action-btn site-action-danger liquid-button"
                        onClick={() => onRemove(app.id)}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
